import React from "react";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";

/**
 * Skeleton matching Appointment page layout (header, tabs, stats, details, patient card, QR).
 * Accepts darkMode boolean to match theme.
 */
const SmallShimmer = ({ darkMode }) => (
  <div
    className={`absolute inset-0 `}
    style={{ pointerEvents: "none" }}
  />
);

const HeaderSkeleton = ({ darkMode }) => (
  <div className="mb-6 sm:mb-8">
    <div className={`h-5 sm:h-6 w-24 sm:w-28 rounded-md sm:rounded-lg mb-3 sm:mb-4 ${darkMode ? "bg-gray-800" : "bg-gray-200"} animate-pulse`} />
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0">
      <div className="flex-1">
        <div className={`h-8 sm:h-10 w-40 sm:w-56 rounded-md sm:rounded-lg mb-2 ${darkMode ? "bg-gray-800" : "bg-gray-200"} animate-pulse`} />
        <div className={`h-4 sm:h-5 w-60 sm:w-80 rounded-md sm:rounded-lg ${darkMode ? "bg-gray-800/70" : "bg-gray-200/70"} animate-pulse`} />
      </div>
      <div className={`h-8 sm:h-10 w-28 sm:w-32 rounded-lg sm:rounded-xl ${darkMode ? "bg-gray-800" : "bg-gray-200"} animate-pulse`} />
    </div>
  </div>
);

const TabsSkeleton = ({ darkMode }) => (
  <div className="flex gap-2 sm:gap-3 mb-6 sm:mb-8 flex-wrap">
    {[...Array(3)].map((_, i) => (
      <div key={i} className={`px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg sm:rounded-xl ${darkMode ? "bg-gray-800 border border-gray-700" : "bg-gray-200 border border-gray-300"} h-9 sm:h-11 w-28 sm:w-36 relative overflow-hidden`}>
        <SmallShimmer darkMode={darkMode} />
      </div>
    ))}
  </div>
);

const StatCardSkeleton = ({ darkMode }) => (
  <div className={`p-3 sm:p-4 rounded-lg sm:rounded-xl ${darkMode ? "bg-gray-800/40 border border-gray-700" : "bg-white border border-gray-200"} animate-pulse`}>
    <div className={`h-4 sm:h-5 w-24 sm:w-28 rounded ${darkMode ? "bg-gray-700" : "bg-gray-200"}`} />
    <div className={`h-6 sm:h-8 w-16 sm:w-20 rounded mt-2 sm:mt-3 ${darkMode ? "bg-gray-700" : "bg-gray-200"}`} />
  </div>
);

const CardBlockSkeleton = ({ darkMode, height = "h-32 sm:h-40" }) => (
  <div className={`rounded-xl sm:rounded-2xl border p-3 sm:p-4 ${darkMode ? "bg-gray-900/40 border-gray-700" : "bg-white border-gray-200"} overflow-hidden`}>
    <SmallShimmer darkMode={darkMode} />
    <div className="space-y-2 sm:space-y-3 relative z-10">
      <div className={`h-4 sm:h-5 w-36 sm:w-48 rounded ${darkMode ? "bg-gray-700" : "bg-gray-200"} animate-pulse`} />
      <div className={`${height} rounded-md sm:rounded-lg mt-2 ${darkMode ? "bg-gray-700/70" : "bg-gray-200/70"} animate-pulse`} />
    </div>
  </div>
);

const PatientSummarySkeleton = ({ darkMode }) => (
  <div className={`rounded-xl sm:rounded-2xl border p-3 sm:p-4 ${darkMode ? "bg-gray-900/40 border-gray-700" : "bg-white border-gray-200"} overflow-hidden`}>
    <SmallShimmer darkMode={darkMode} />
    <div className="flex items-center gap-3 sm:gap-4 relative z-10">
      <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full ${darkMode ? "bg-gray-700" : "bg-gray-200"} animate-pulse`} />
      <div className="flex-1">
        <div className={`h-4 sm:h-5 w-32 sm:w-40 rounded ${darkMode ? "bg-gray-700" : "bg-gray-200"} animate-pulse`} />
        <div className={`h-3 sm:h-4 w-24 sm:w-32 rounded mt-1.5 sm:mt-2 ${darkMode ? "bg-gray-700/70" : "bg-gray-200/70"} animate-pulse`} />
        <div className="flex items-center gap-2 mt-2 sm:mt-3">
          <div className={`h-2.5 sm:h-3 w-16 sm:w-20 rounded ${darkMode ? "bg-gray-700" : "bg-gray-200"} animate-pulse`} />
          <div className={`h-2.5 sm:h-3 w-10 sm:w-12 rounded ${darkMode ? "bg-gray-700" : "bg-gray-200"} animate-pulse`} />
        </div>
      </div>
    </div>
    <div className="mt-3 sm:mt-4">
      <div className={`h-28 sm:h-36 rounded-md sm:rounded-lg ${darkMode ? "bg-gray-700/70" : "bg-gray-200/70"} animate-pulse`} />
    </div>
  </div>
);

/**
 * Main exported skeleton that mirrors the appointment page layout.
 * Use where appointmentLoading && !appointment was previously used.
 */
const ViewPageSkeleton = () => {

    const darkMode = useSelector((state) => state.settings.darkMode);
  return (
    <div className={`min-h-screen transition-colors duration-200 `}>
      <div className="p-4 sm:p-6">
        <HeaderSkeleton darkMode={darkMode} />
        <TabsSkeleton darkMode={darkMode} />

        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <StatCardSkeleton darkMode={darkMode} />
          <StatCardSkeleton darkMode={darkMode} />
          <StatCardSkeleton darkMode={darkMode} />
          <StatCardSkeleton darkMode={darkMode} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <div className="space-y-4 sm:space-y-6">
            <CardBlockSkeleton darkMode={darkMode} height="h-36 sm:h-44" />
            <CardBlockSkeleton darkMode={darkMode} height="h-48 sm:h-64" />
          </div>

          <div className="space-y-4 sm:space-y-6">
            <PatientSummarySkeleton darkMode={darkMode} />
            <div className={`rounded-xl sm:rounded-2xl border p-3 sm:p-4 ${darkMode ? "bg-gray-900/40 border-gray-700" : "bg-white border-gray-200"} overflow-hidden`}>
              <SmallShimmer darkMode={darkMode} />
              <div className="relative z-10">
                <div className={`h-4 sm:h-5 w-32 sm:w-40 rounded ${darkMode ? "bg-gray-700" : "bg-gray-200"} animate-pulse`} />
                <div className="mt-3 sm:mt-4 grid grid-cols-2 gap-2 sm:gap-3">
                  <div className={`h-16 sm:h-20 rounded ${darkMode ? "bg-gray-700/70" : "bg-gray-200/70"} animate-pulse`} />
                  <div className={`h-16 sm:h-20 rounded ${darkMode ? "bg-gray-700/70" : "bg-gray-200/70"} animate-pulse`} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 sm:mt-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className={`rounded-lg sm:rounded-xl border p-3 sm:p-4 ${darkMode ? "bg-gray-900/30 border-gray-700" : "bg-white border-gray-200"} animate-pulse`}>
              <div className={`h-3.5 sm:h-4 w-24 sm:w-28 rounded ${darkMode ? "bg-gray-700" : "bg-gray-200"} mb-2 sm:mb-3`} />
              <div className={`h-28 sm:h-36 rounded ${darkMode ? "bg-gray-700/70" : "bg-gray-200/70"}`} />
            </div>
          ))}
        </div>

        <div className="mt-4 sm:mt-6 text-center sm:text-right">
          <div className={`inline-block rounded px-3 py-1.5 sm:px-3 sm:py-2 text-sm sm:text-base ${darkMode ? "bg-gray-800 text-gray-300" : "bg-gray-100 text-gray-700"}`}>
            Loading appointment…
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewPageSkeleton;